import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import * as fmt from '@/i18n/format.ts'
import { detectLocale, type Locale, persistLocale } from '@/i18n/locale.ts'
import { catalogs, type Messages } from '@/i18n/messages/index.ts'

/**
 * The active language, and everything bound to it.
 *
 * ── Where this mounts, and why it is not negotiable ──
 *
 * `main.tsx`, between `QueryClientProvider` and `RouterProvider` (PRD-27 D-28). There is no
 * `App.tsx` in this codebase and the router is built at module scope; `/login` and the
 * catch-all sit OUTSIDE `RequireAuth`/`AppShell`. A provider mounted in the shell — the
 * obvious place — would silently miss the sign-in page, which is the first thing a new
 * deployment is judged by and the one screen a person reads before they have an account
 * whose locale we could have known.
 *
 * ── What switching does, and what it deliberately does not ──
 *
 * It re-renders. It does not refetch, navigate or reset: no server response in this product
 * depends on the locale, so a switch is pure presentation. An unsent composer draft, an open
 * dialog and an answer streaming over SSE all survive it (LOC-007).
 *
 * The one thing that does NOT flip is text already produced — a completed answer keeps the
 * language it was written in, because it is the model's output and therefore data (D-3,
 * D-17). Everything derived per render follows the switch immediately.
 *
 * ── The formatters ──
 *
 * `format.ts` stays pure and takes a locale; this binds the active one so call sites read
 * `formatBytes(n)` rather than threading a locale through every component. The two time
 * helpers additionally get their catalogue words here — `Intl` has no opinion worth having
 * about "just now" — which is why they are parameters on a pure module rather than imports
 * inside it.
 */

/**
 * One explicit choice of language, as an EVENT. `seq` is what makes two consecutive choices
 * of the same language two events, and what lets a subscriber know which one it has handled.
 */
export interface LocaleChoice {
  locale: Locale
  seq: number
}

export interface I18n {
  locale: Locale
  /**
   * The PERSON'S choice — what the switcher calls. It changes the tab and raises `choice`.
   *
   * Kept apart from `adoptLocale` because the two are different facts that produce the same
   * state. The first cut of the account sync had one setter and tried to recover which had
   * happened by comparing the tab to the account afterwards; a comparison cannot say who
   * moved, so two effects each concluded the other side was out of date and flipped the
   * language ~70 times a second until a request happened to land last.
   */
  setLocale: (locale: Locale) => void
  /**
   * The ACCOUNT'S language arriving in this tab — chosen on another device, or read at
   * sign-in. It changes the tab and raises nothing, because it is not a decision made here
   * and must never be written back as one.
   */
  adoptLocale: (locale: Locale) => void
  /** The latest explicit choice made in this tab, or null if the person has made none. */
  choice: LocaleChoice | null
  t: Messages
  formatDate: (value: Date | string | number) => string
  formatDayMonth: (value: Date | string | number) => string
  formatDateTime: (value: Date | string | number) => string
  formatTime: (value: Date | string | number) => string
  formatWeekday: (value: Date | string | number) => string
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
  formatCount: (value: number) => string
  formatPercent: (fraction: number, maximumFractionDigits?: number) => string
  formatCost: (usd: number) => string
  formatTokens: (count: number) => string
  formatBytes: (bytes: number) => string
  formatDuration: (ms: number | null | undefined) => string
  formatSeconds: (ms: number) => string
  formatList: (items: readonly string[]) => string
  formatListOr: (items: readonly string[]) => string
  formatAgo: (value: Date | string | number, nowMs?: number) => string
  formatUntil: (value: Date | string | number, nowMs?: number) => string
  compare: (a: string, b: string) => number
}

const I18nContext = createContext<I18n | null>(null)

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? detectLocale())

  useEffect(() => {
    // Not a mirror of the locale — it IS the locale, and it is what a screen reader, a
    // spellchecker and a hyphenation engine read (LOC-006). Without it a screen reader
    // announces Portuguese with English phonemes, which is unintelligible rather than
    // merely wrong.
    document.documentElement.lang = locale
  }, [locale])

  const [choice, setChoice] = useState<LocaleChoice | null>(null)

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    persistLocale(next)
    // Raised even when `next` is already the language on screen: pressing the active option
    // is how somebody turns an inference about them into a decision by them.
    setChoice((previous) => ({ locale: next, seq: (previous?.seq ?? 0) + 1 }))
  }, [])

  const adoptLocale = useCallback((next: Locale) => {
    setLocaleState(next)
    // Still mirrored to storage: the mirror is what `boot.js` paints from on the next cold
    // load, and it should paint the account's language, not the one this tab used to be in.
    persistLocale(next)
  }, [])

  const value = useMemo<I18n>(() => {
    const t = catalogs[locale]
    return {
      locale,
      setLocale,
      adoptLocale,
      choice,
      t,
      formatDate: (v) => fmt.formatDate(locale, v),
      formatDayMonth: (v) => fmt.formatDayMonth(locale, v),
      formatDateTime: (v) => fmt.formatDateTime(locale, v),
      formatTime: (v) => fmt.formatTime(locale, v),
      formatWeekday: (v) => fmt.formatWeekday(locale, v),
      formatNumber: (v, options) => fmt.formatNumber(locale, v, options),
      formatCount: (v) => fmt.formatCount(locale, v),
      formatPercent: (v, digits) => fmt.formatPercent(locale, v, digits),
      formatCost: (v) => fmt.formatCost(locale, v),
      formatTokens: (v) => fmt.formatTokens(locale, v),
      formatBytes: (v) => fmt.formatBytes(locale, v),
      formatDuration: (v) => fmt.formatDuration(locale, v),
      formatSeconds: (v) => fmt.formatSeconds(locale, v),
      formatList: (items) => fmt.formatList(locale, items),
      formatListOr: (items) => fmt.formatListOr(locale, items),
      formatAgo: (v, nowMs) => fmt.formatAgo(locale, v, { nowMs, justNow: t.common.justNow }),
      formatUntil: (v, nowMs) =>
        fmt.formatUntil(locale, v, { nowMs, expired: t.common.expired, underAMinute: t.common.inUnderAMinute }),
      compare: (a, b) => fmt.collator(locale).compare(a, b)
    }
  }, [locale, setLocale, adoptLocale, choice])

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export function useI18n(): I18n {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

/**
 * Convenience for the overwhelmingly common case — a component that wants words and no
 * formatters. Kept separate so that `const { t } = useI18n()` is not the only idiom and
 * `useT()` reads like what it does at a call site.
 */
export function useT(): Messages {
  return useI18n().t
}
